import React, { useEffect, useState, useRef, useCallback } from 'react';
import styles from './StarEffect.module.css';

interface Star {
  id: number;
  x: number;
  y: number;
  size: number;
  speed: number;
  rotation: number;
  opacity: number;
  rotationSpeed: number;
}

const MAX_STARS = 30; // 별 개수 감소
const CREATION_INTERVAL = 500; // 생성 간격 증가
const INITIAL_STARS = 10; // 초기 별 개수 감소

const StarEffect: React.FC = () => {
  const [stars, setStars] = useState<Star[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastTimeRef = useRef<number>(0);
  const frameInterval = 1000 / 60;
  const starCreationInterval = useRef<NodeJS.Timeout | undefined>(undefined);
  const starsRef = useRef<Star[]>([]);

  const createStar = useCallback(() => {
    if (starsRef.current.length >= MAX_STARS) {
      return; // 최대 개수 도달 시 생성 중단
    }

    const windowWidth = window.innerWidth;
    const newStar: Star = {
      id: Date.now() + Math.random(),
      x: Math.random() * windowWidth,
      y: window.innerHeight + 50,
      size: Math.random() * 20 + 10,
      speed: -(Math.random() * 3 + 1),
      rotation: Math.random() * 360,
      opacity: Math.random() * 0.5 + 0.5,
      rotationSpeed: Math.random() * 2 + 1,
    };

    setStars(prevStars => {
      const newStars = [...prevStars, newStar];
      starsRef.current = newStars;
      return newStars;
    });
  }, []);

  const removeOldStars = useCallback(() => {
    setStars(prevStars => {
      const newStars = prevStars.filter(star => star.y > -star.size);
      starsRef.current = newStars;
      return newStars;
    });
  }, []);

  const animateStars = useCallback((timestamp: number) => {
    if (!lastTimeRef.current) {
      lastTimeRef.current = timestamp;
    }

    const deltaTime = timestamp - lastTimeRef.current;

    if (deltaTime >= frameInterval) {
      setStars(prevStars => {
        const newStars = prevStars.map(star => ({
          ...star,
          y: star.y + star.speed,
          rotation: star.rotation + (star.rotationSpeed * deltaTime) / 16,
        }));
        starsRef.current = newStars;
        return newStars;
      });

      lastTimeRef.current = timestamp;
      removeOldStars();
    }

    animationFrameRef.current = requestAnimationFrame(animateStars);
  }, [frameInterval, removeOldStars]);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    if (starCreationInterval.current) {
      clearInterval(starCreationInterval.current);
      starCreationInterval.current = undefined;
    }
    setStars([]);
    starsRef.current = [];
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cleanup();
      } else {
        for (let i = 0; i < INITIAL_STARS; i++) {
          createStar();
        }
        starCreationInterval.current = setInterval(createStar, CREATION_INTERVAL);
        animationFrameRef.current = requestAnimationFrame(animateStars);
      }
    };

    handleVisibilityChange(); // 초기 실행
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanup();
    };
  }, [cleanup, createStar, animateStars]);

  return (
    <div className={styles.starContainer}>
      {stars.map(star => (
        <img
          key={star.id}
          src="/images/star.png"
          alt="star"
          className={styles.star}
          style={{
            left: `${star.x}px`,
            top: `${star.y}px`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            transform: `translate3d(0,0,0) rotate(${star.rotation}deg)`,
            opacity: star.opacity,
          }}
        />
      ))}
    </div>
  );
};

export default StarEffect;
